import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { BookOpen, Search, PlayCircle, CheckCircle2, Circle, ArrowLeft, LayoutDashboard, MonitorPlay, Layers, Sparkles, Clock, Award, ChevronRight, HelpCircle } from 'lucide-react';
import { courses, quickLinks, Course, Lesson, QuizQuestion } from '../data/helpData';
// Map string icon names to actual Lucide components
const iconMap: Record<string, React.ElementType> = {
  LayoutDashboard,
  MonitorPlay,
  Layers,
  Sparkles
};
// Color mapping for Tailwind classes
const colorMap: Record<string, {
  bg: string;
  text: string;
  border: string;
  ring: string;
}> = {
  blue: {
    bg: 'bg-blue-50',
    text: 'text-blue-600',
    border: 'border-blue-200',
    ring: 'ring-blue-500'
  },
  purple: {
    bg: 'bg-purple-50',
    text: 'text-purple-600',
    border: 'border-purple-200',
    ring: 'ring-purple-500'
  },
  emerald: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-600',
    border: 'border-emerald-200',
    ring: 'ring-emerald-500'
  },
  amber: {
    bg: 'bg-amber-50',
    text: 'text-amber-600',
    border: 'border-amber-200',
    ring: 'ring-amber-500'
  }
};
export function HelpCenterPage() {
  // View state: 'browse' | 'course' | 'quiz'
  const [view, setView] = useState<'browse' | 'course' | 'quiz'>('browse');
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  // Quiz state
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, number>>({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [videoWatched, setVideoWatched] = useState(false);
  // Progress state (persisted in localStorage)
  const [completedLessons, setCompletedLessons] = useState<Record<string, boolean>>({});
  // Load progress on mount
  useEffect(() => {
    const saved = localStorage.getItem('resvr-help-progress');
    if (saved) {
      try {
        setCompletedLessons(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse progress', e);
      }
    }
  }, []);
  // Save progress when it changes
  const markLessonComplete = (lessonId: string) => {
    const newProgress = {
      ...completedLessons,
      [lessonId]: true
    };
    setCompletedLessons(newProgress);
    localStorage.setItem('resvr-help-progress', JSON.stringify(newProgress));
  };
  // Navigation handlers
  const handleOpenCourse = (course: Course, lessonId?: string) => {
    setSelectedCourse(course);
    setSelectedLesson(lessonId ? course.lessons.find(l => l.id === lessonId) || course.lessons[0] : course.lessons[0]);
    setVideoWatched(false);
    setView('course');
    window.scrollTo(0, 0);
  };
  const handleBackToBrowse = () => {
    setSelectedCourse(null);
    setSelectedLesson(null);
    setView('browse');
    window.scrollTo(0, 0);
  };
  const handleStartQuiz = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswers({});
    setQuizSubmitted(false);
    setView('quiz');
  };
  const handleSubmitQuiz = () => {
    setQuizSubmitted(true);
    // Check if passed (all correct)
    if (selectedLesson?.quiz) {
      const allCorrect = selectedLesson.quiz.every(q => selectedAnswers[q.id] === q.correctIndex);
      if (allCorrect) {
        markLessonComplete(selectedLesson.id);
      }
    }
  };
  // Derived stats
  const totalLessons = courses.reduce((acc, course) => acc + course.lessons.length, 0);
  const completedCount = Object.values(completedLessons).filter(Boolean).length;
  const progressPercentage = Math.round(completedCount / totalLessons * 100) || 0;
  // Filtered courses
  const filteredCourses = courses.filter(c => c.title.toLowerCase().includes(searchQuery.toLowerCase()) || c.description.toLowerCase().includes(searchQuery.toLowerCase()));
  const dashboardCourses = filteredCourses.filter(c => c.track === 'dashboard');
  const visualizerCourses = filteredCourses.filter(c => c.track === 'visualizer');
  // --- BROWSE VIEW ---
  if (view === 'browse') {
    return <div className="max-w-7xl mx-auto space-y-10 pb-12" data-id="element-5140">
        {/* Hero Section */}
        <div className="bg-slate-900 rounded-2xl p-8 md:p-12 text-white relative overflow-hidden" data-id="element-5141">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 rounded-full bg-primary-500/20 blur-3xl pointer-events-none" data-id="element-5142" />
          <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 rounded-full bg-blue-500/20 blur-3xl pointer-events-none" data-id="element-5143" />

          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-8" data-id="element-5144">
            <div className="max-w-2xl" data-id="element-5145">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-primary-300 text-sm font-medium mb-6 border border-white/10" data-id="element-5146">
                <BookOpen className="h-4 w-4" data-id="element-5147" />
                ResVR Learning Center
              </div>
              <h1 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-5148">
                Master your workflow.
              </h1>
              <p className="text-slate-300 text-lg mb-8 max-w-xl" data-id="element-5149">
                Everything you need to know to get the most out of the ResVR
                dashboard and Unreal Engine visualizer.
              </p>

              <div className="relative max-w-md" data-id="element-5150">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" data-id="element-5151" />
                <input type="text" placeholder="Search for topics, guides, or features..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white/15 transition-all" data-id="element-5152" />
              </div>
            </div>

            {/* Progress Widget */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/10 rounded-xl p-6 flex items-center gap-6 min-w-[280px]" data-id="element-5153">
              <div className="relative w-20 h-20 flex-shrink-0" data-id="element-5154">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100" data-id="element-5155">
                  <circle cx="50" cy="50" r="40" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-white/10" data-id="element-5156" />
                  <circle cx="50" cy="50" r="40" stroke="currentColor" strokeWidth="8" fill="transparent" strokeDasharray={`${2 * Math.PI * 40}`} strokeDashoffset={`${2 * Math.PI * 40 * (1 - progressPercentage / 100)}`} className="text-primary-400 transition-all duration-1000 ease-out" strokeLinecap="round" data-id="element-5157" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-5158">
                  <span className="text-xl font-bold" data-id="element-5159">
                    {progressPercentage}%
                  </span>
                </div>
              </div>
              <div data-id="element-5160">
                <h3 className="font-semibold text-lg mb-1" data-id="element-5161">Your Progress</h3>
                <p className="text-slate-300 text-sm" data-id="element-5162">
                  {completedCount} of {totalLessons} lessons completed
                </p>
                {progressPercentage === 100 && <Badge variant="success" className="mt-2 bg-emerald-500/20 text-emerald-300 border-emerald-500/30" data-id="element-5163">
                    <Award className="h-3 w-3 mr-1" data-id="element-5164" /> All clear!
                  </Badge>}
              </div>
            </div>
          </div>
        </div>

        {/* Dashboard Track */}
        {dashboardCourses.length > 0 && <div className="space-y-6" data-id="element-5165">
            <div className="flex items-center gap-3 border-b border-slate-200 pb-4" data-id="element-5166">
              <div className="p-2 bg-blue-100 text-blue-600 rounded-lg" data-id="element-5167">
                <LayoutDashboard className="h-5 w-5" data-id="element-5168" />
              </div>
              <div data-id="element-5169">
                <h2 className="text-xl font-bold text-slate-900" data-id="element-5170">
                  Dashboard Essentials
                </h2>
                <p className="text-sm text-slate-500" data-id="element-5171">
                  Master the web platform and manage your communities
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-5172">
              {dashboardCourses.map(course => <CourseCard key={course.id} course={course} completedLessons={completedLessons} onClick={() => handleOpenCourse(course)} data-id="element-5173" />)}
            </div>
          </div>}

        {/* Visualizer Track */}
        {visualizerCourses.length > 0 && <div className="space-y-6" data-id="element-5174">
            <div className="flex items-center gap-3 border-b border-slate-200 pb-4" data-id="element-5175">
              <div className="p-2 bg-emerald-100 text-emerald-600 rounded-lg" data-id="element-5176">
                <MonitorPlay className="h-5 w-5" data-id="element-5177" />
              </div>
              <div data-id="element-5178">
                <h2 className="text-xl font-bold text-slate-900" data-id="element-5179">
                  ResVR Visualizer Mastery
                </h2>
                <p className="text-sm text-slate-500" data-id="element-5180">
                  Learn to present and sell using the Unreal Engine app
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-5181">
              {visualizerCourses.map(course => <CourseCard key={course.id} course={course} completedLessons={completedLessons} onClick={() => handleOpenCourse(course)} data-id="element-5182" />)}
            </div>
          </div>}

        {/* Quick Help */}
        {!searchQuery && <div className="bg-slate-50 rounded-xl p-8 border border-slate-200" data-id="element-5183">
            <div className="flex items-center gap-2 mb-6" data-id="element-5184">
              <HelpCircle className="h-5 w-5 text-slate-400" data-id="element-5185" />
              <h3 className="text-lg font-bold text-slate-900" data-id="element-5186">
                Quick Answers
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-5187">
              {quickLinks.map(link => {
            const course = courses.find(c => c.id === link.courseId);
            return <button key={link.id} onClick={() => course && handleOpenCourse(course, link.lessonId)} className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-lg hover:border-primary-300 hover:shadow-sm transition-all text-left group" data-id="element-5188">
                    <span className="font-medium text-slate-700 group-hover:text-primary-600 transition-colors" data-id="element-5189">
                      {link.title}
                    </span>
                    <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-primary-500 transition-colors" data-id="element-5190" />
                  </button>;
          })}
            </div>
          </div>}
      </div>;
  }
  // --- COURSE DETAIL VIEW ---
  if (view === 'course' && selectedCourse && selectedLesson) {
    const Icon = iconMap[selectedCourse.icon] || BookOpen;
    const colors = colorMap[selectedCourse.color] || colorMap.blue;
    const courseCompletedCount = selectedCourse.lessons.filter(l => completedLessons[l.id]).length;
    const courseProgress = Math.round(courseCompletedCount / selectedCourse.lessons.length * 100);
    const isCurrentLessonCompleted = completedLessons[selectedLesson.id];
    return <div className="max-w-7xl mx-auto space-y-6 pb-12" data-id="element-5191">
        <button onClick={handleBackToBrowse} className="flex items-center text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors" data-id="element-5192">
          <ArrowLeft className="h-4 w-4 mr-1" data-id="element-5193" />
          Back to Learning Center
        </button>

        {/* Course Header */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-5194">
          <div className="flex items-center gap-4" data-id="element-5195">
            <div className={`p-4 rounded-xl ${colors.bg} ${colors.text}`} data-id="element-5196">
              <Icon className="h-8 w-8" data-id="element-5197" />
            </div>
            <div data-id="element-5198">
              <div className="flex items-center gap-2 mb-1" data-id="element-5199">
                <Badge variant="secondary" className="uppercase tracking-wider text-[10px]" data-id="element-5200">
                  {selectedCourse.track === 'dashboard' ? 'Dashboard' : 'Visualizer'}
                </Badge>
                <span className="text-sm text-slate-500" data-id="element-5201">
                  {selectedCourse.lessons.length} lessons
                </span>
              </div>
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-5202">
                {selectedCourse.title}
              </h1>
            </div>
          </div>

          <div className="w-full md:w-64" data-id="element-5203">
            <div className="flex justify-between text-sm mb-2" data-id="element-5204">
              <span className="font-medium text-slate-700" data-id="element-5205">
                Course Progress
              </span>
              <span className="text-slate-500" data-id="element-5206">{courseProgress}%</span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2" data-id="element-5207">
              <div className={`h-2 rounded-full ${colors.bg.replace('50', '500')}`} style={{
              width: `${courseProgress}%`
            }} data-id="element-5208" />
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 items-start" data-id="element-5209">
          {/* Sidebar - Lesson List */}
          <div className="w-full lg:w-80 flex-shrink-0 space-y-2" data-id="element-5210">
            <h3 className="font-semibold text-slate-900 mb-4 px-2" data-id="element-5211">
              Course Content
            </h3>
            {selectedCourse.lessons.map((lesson, index) => {
            const isSelected = lesson.id === selectedLesson.id;
            const isCompleted = completedLessons[lesson.id];
            return <button key={lesson.id} onClick={() => {
              setSelectedLesson(lesson);
              setVideoWatched(false);
              window.scrollTo(0, 0);
            }} className={`w-full flex items-start gap-3 p-3 rounded-lg text-left transition-all ${isSelected ? `${colors.bg} ${colors.ring} ring-1` : 'hover:bg-slate-50'}`} data-id="element-5212">
                  <div className="mt-0.5 flex-shrink-0" data-id="element-5213">
                    {isCompleted ? <CheckCircle2 className={`h-5 w-5 ${colors.text}`} data-id="element-5214" /> : <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center text-[10px] font-bold ${isSelected ? `${colors.border} ${colors.text}` : 'border-slate-300 text-slate-400'}`} data-id="element-5215">
                        {index + 1}
                      </div>}
                  </div>
                  <div data-id="element-5216">
                    <p className={`font-medium text-sm ${isSelected ? 'text-slate-900' : 'text-slate-700'}`} data-id="element-5217">
                      {lesson.title}
                    </p>
                    <div className="flex items-center gap-2 mt-1 text-xs text-slate-500" data-id="element-5218">
                      <span className="flex items-center gap-1" data-id="element-5219">
                        <Clock className="h-3 w-3" data-id="element-5220" /> {lesson.duration}
                      </span>
                      {lesson.quiz && <>
                          <span data-id="element-5221">•</span>
                          <span className="flex items-center gap-1" data-id="element-5222">
                            <HelpCircle className="h-3 w-3" data-id="element-5223" /> Quiz
                          </span>
                        </>}
                    </div>
                  </div>
                </button>;
          })}
          </div>

          {/* Main Content Area */}
          <div className="flex-1 w-full min-w-0" data-id="element-5224">
            {/* Video Player Placeholder */}
            <div className="w-full aspect-video bg-slate-900 rounded-xl overflow-hidden relative group cursor-pointer shadow-lg mb-6" onClick={() => setVideoWatched(true)} data-id="element-5225">
              {selectedLesson.thumbnailUrl && <img src={selectedLesson.thumbnailUrl} alt={selectedLesson.title} className={`w-full h-full object-cover transition-all duration-500 ${videoWatched ? 'opacity-30 scale-105' : 'opacity-60 group-hover:opacity-50 group-hover:scale-105'}`} data-id="element-5226" />}

              {!videoWatched ? <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-5227">
                  <div className="h-16 w-16 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-5228">
                    <PlayCircle className="h-10 w-10 text-white" data-id="element-5229" />
                  </div>
                  <p className="text-white font-medium mt-4 drop-shadow-md" data-id="element-5230">
                    Click to play lesson
                  </p>
                </div> : <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-5231">
                  <div className="h-16 w-16 bg-emerald-500/20 backdrop-blur-md rounded-full flex items-center justify-center mb-4" data-id="element-5232">
                    <CheckCircle2 className="h-10 w-10 text-emerald-400" data-id="element-5233" />
                  </div>
                  <p className="text-white font-medium text-lg" data-id="element-5234">
                    Video Completed
                  </p>
                  <Button variant="secondary" size="sm" className="mt-4 bg-white/10 hover:bg-white/20 text-white border-white/20" onClick={e => {
                e.stopPropagation();
                setVideoWatched(false);
              }} data-id="element-5235">
                    Watch Again
                  </Button>
                </div>}
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-8" data-id="element-5236">
              <div className="flex items-start justify-between gap-4 mb-6" data-id="element-5237">
                <div data-id="element-5238">
                  <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-5239">
                    {selectedLesson.title}
                  </h2>
                  <p className="text-slate-600 leading-relaxed" data-id="element-5240">
                    {selectedLesson.description}
                  </p>
                </div>
                {isCurrentLessonCompleted && <Badge variant="success" className="flex-shrink-0" data-id="element-5241">
                    <CheckCircle2 className="h-3 w-3 mr-1" data-id="element-5242" /> Completed
                  </Badge>}
              </div>

              <div className="border-t border-slate-100 pt-6 flex items-center justify-between" data-id="element-5243">
                {selectedLesson.quiz ? <div className="flex items-center gap-4" data-id="element-5244">
                    <div className="p-2 bg-amber-50 rounded-lg" data-id="element-5245">
                      <HelpCircle className="h-5 w-5 text-amber-600" data-id="element-5246" />
                    </div>
                    <div data-id="element-5247">
                      <p className="font-medium text-slate-900" data-id="element-5248">
                        Knowledge Check
                      </p>
                      <p className="text-sm text-slate-500" data-id="element-5249">
                        {selectedLesson.quiz.length} questions to complete this
                        lesson
                      </p>
                    </div>
                  </div> : <div className="text-sm text-slate-500" data-id="element-5250">
                    No quiz for this lesson. Just mark it complete when you're
                    done!
                  </div>}

                {selectedLesson.quiz ? <Button onClick={handleStartQuiz} disabled={!videoWatched && !isCurrentLessonCompleted} variant={isCurrentLessonCompleted ? 'outline' : 'primary'} data-id="element-5251">
                    {isCurrentLessonCompleted ? 'Retake Quiz' : 'Take Quiz'}
                  </Button> : <Button onClick={() => markLessonComplete(selectedLesson.id)} disabled={!videoWatched || isCurrentLessonCompleted} variant={isCurrentLessonCompleted ? 'outline' : 'primary'} leftIcon={isCurrentLessonCompleted ? <CheckCircle2 className="h-4 w-4" data-id="element-5253" /> : undefined} data-id="element-5252">
                    {isCurrentLessonCompleted ? 'Completed' : 'Mark as Complete'}
                  </Button>}
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  // --- QUIZ VIEW ---
  if (view === 'quiz' && selectedCourse && selectedLesson && selectedLesson.quiz) {
    const questions = selectedLesson.quiz;
    const currentQuestion = questions[currentQuestionIndex];
    const isLastQuestion = currentQuestionIndex === questions.length - 1;
    const hasAnsweredCurrent = selectedAnswers[currentQuestion.id] !== undefined;
    // Calculate results if submitted
    let score = 0;
    if (quizSubmitted) {
      score = questions.filter(q => selectedAnswers[q.id] === q.correctIndex).length;
    }
    const passed = score === questions.length;
    return <div className="max-w-3xl mx-auto space-y-6 pb-12 pt-8" data-id="element-5254">
        <button onClick={() => setView('course')} className="flex items-center text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors mb-8" data-id="element-5255">
          <ArrowLeft className="h-4 w-4 mr-1" data-id="element-5256" />
          Back to Lesson
        </button>

        {!quizSubmitted ? <Card className="border-slate-200 shadow-sm" data-id="element-5257">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50 pb-4" data-id="element-5258">
              <div className="flex items-center justify-between mb-2" data-id="element-5259">
                <Badge variant="secondary" data-id="element-5260">Knowledge Check</Badge>
                <span className="text-sm font-medium text-slate-500" data-id="element-5261">
                  Question {currentQuestionIndex + 1} of {questions.length}
                </span>
              </div>
              <CardTitle className="text-xl leading-snug pt-2" data-id="element-5262">
                {currentQuestion.question}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 pb-8 px-8" data-id="element-5263">
              <div className="space-y-3" data-id="element-5264">
                {currentQuestion.options.map((option, index) => {
              const isSelected = selectedAnswers[currentQuestion.id] === index;
              return <button key={index} onClick={() => setSelectedAnswers({
                ...selectedAnswers,
                [currentQuestion.id]: index
              })} className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all ${isSelected ? 'border-primary-500 bg-primary-50 text-primary-900' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700'}`} data-id="element-5265">
                      <div className={`flex-shrink-0 h-5 w-5 rounded-full border flex items-center justify-center ${isSelected ? 'border-primary-500' : 'border-slate-300'}`} data-id="element-5266">
                        {isSelected && <div className="h-2.5 w-2.5 rounded-full bg-primary-500" data-id="element-5267" />}
                      </div>
                      <span className="font-medium" data-id="element-5268">{option}</span>
                    </button>;
            })}
              </div>

              <div className="mt-8 flex justify-end" data-id="element-5269">
                {isLastQuestion ? <Button onClick={handleSubmitQuiz} disabled={!hasAnsweredCurrent} size="lg" data-id="element-5270">
                    Submit Answers
                  </Button> : <Button onClick={() => setCurrentQuestionIndex(prev => prev + 1)} disabled={!hasAnsweredCurrent} rightIcon={<ChevronRight className="h-4 w-4" data-id="element-5272" />} data-id="element-5271">
                    Next Question
                  </Button>}
              </div>
            </CardContent>
          </Card> : <Card className="border-slate-200 shadow-sm text-center py-12 px-8" data-id="element-5273">
            <div className={`mx-auto h-20 w-20 rounded-full flex items-center justify-center mb-6 ${passed ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`} data-id="element-5274">
              {passed ? <Award className="h-10 w-10" data-id="element-5275" /> : <HelpCircle className="h-10 w-10" data-id="element-5276" />}
            </div>

            <h2 className="text-3xl font-bold text-slate-900 mb-2" data-id="element-5277">
              {passed ? 'Great job!' : 'Almost there!'}
            </h2>
            <p className="text-lg text-slate-600 mb-8" data-id="element-5278">
              You scored{' '}
              <span className="font-bold text-slate-900" data-id="element-5279">
                {score} out of {questions.length}
              </span>
              .
              {passed ? ' This lesson is now marked as complete.' : ' Review the material and try again to complete the lesson.'}
            </p>

            <div className="flex justify-center gap-4" data-id="element-5280">
              {!passed && <Button variant="outline" onClick={handleStartQuiz} data-id="element-5281">
                  Retake Quiz
                </Button>}
              <Button onClick={() => setView('course')} data-id="element-5282">Back to Lesson</Button>
            </div>
          </Card>}
      </div>;
  }
  return null;
}
// Helper component for Course Cards
function CourseCard({
  course,
  completedLessons,
  onClick
}: {
  course: Course;
  completedLessons: Record<string, boolean>;
  onClick: () => void;
}) {
  const Icon = iconMap[course.icon] || BookOpen;
  const colors = colorMap[course.color] || colorMap.blue;
  const totalLessons = course.lessons.length;
  const completedCount = course.lessons.filter(l => completedLessons[l.id]).length;
  const progress = Math.round(completedCount / totalLessons * 100);
  // Calculate total duration roughly
  const totalMins = course.lessons.reduce((acc, l) => {
    const [m, s] = l.duration.split(':').map(Number);
    return acc + m + s / 60;
  }, 0);
  return <div onClick={onClick} className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-md hover:border-slate-300 transition-all cursor-pointer group flex flex-col h-full" data-id="element-5283">
      <div className="p-6 flex-1" data-id="element-5284">
        <div className={`w-12 h-12 rounded-xl ${colors.bg} ${colors.text} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`} data-id="element-5285">
          <Icon className="h-6 w-6" data-id="element-5286" />
        </div>
        <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-primary-600 transition-colors" data-id="element-5287">
          {course.title}
        </h3>
        <p className="text-sm text-slate-600 line-clamp-2" data-id="element-5288">
          {course.description}
        </p>
      </div>

      <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50" data-id="element-5289">
        <div className="flex items-center justify-between text-xs text-slate-500 mb-3" data-id="element-5290">
          <div className="flex items-center gap-3" data-id="element-5291">
            <span className="flex items-center gap-1" data-id="element-5292">
              <BookOpen className="h-3.5 w-3.5" data-id="element-5293" /> {totalLessons} lessons
            </span>
            <span className="flex items-center gap-1" data-id="element-5294">
              <Clock className="h-3.5 w-3.5" data-id="element-5295" /> {Math.round(totalMins)} mins
            </span>
          </div>
          {progress === 100 && <CheckCircle2 className="h-4 w-4 text-emerald-500" data-id="element-5296" />}
        </div>

        <div className="w-full bg-slate-200 rounded-full h-1.5" data-id="element-5297">
          <div className={`h-1.5 rounded-full transition-all duration-500 ${progress === 100 ? 'bg-emerald-500' : colors.bg.replace('50', '500')}`} style={{
          width: `${progress}%`
        }} data-id="element-5298" />
        </div>
      </div>
    </div>;
}